import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import systemConfigurationService from '@/services/systemConfigurationService';
import { createTramiteWithDefaultSubtramites } from '@/services/tramiteService';

interface CreateTramiteWithSubtramitesParams {
  data: any;
}

interface CreateTramiteResult {
  tramite: any;
  initializedSubtramites: string[];
  errors: string[];
}

export const useDefaultSubtramites = () => {
  const [lastCreationResult, setLastCreationResult] = useState<CreateTramiteResult | null>(null);

  // Query para obtener la configuración de subtramites por defecto
  const {
    data: configuration,
    isLoading: isLoadingConfig,
    isError: configError
  } = useQuery({
    queryKey: ['system-configuration'],
    queryFn: systemConfigurationService.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false
  });

  // Mutation para crear trámite con subtramites por defecto
  const createTramiteWithSubtramitesMutation = useMutation({
    mutationFn: async ({ data }: CreateTramiteWithSubtramitesParams) => {
      // Obtener subtramites por defecto de la configuración
      const defaultSubtramites = configuration?.subtramites_configuration?.default_enabled || [];

      // Usar el servicio mejorado que maneja la creación y inicialización
      const result = await createTramiteWithDefaultSubtramites({
        data,
        defaultSubtramites
      });

      setLastCreationResult(result);
      return result;
    },
    onError: (error) => {
      console.error('Error creating tramite with default subtramites:', error);
      setLastCreationResult(null);
    }
  });

  // Función helper para obtener los subtramites por defecto actuales
  const getDefaultSubtramites = () => {
    return configuration?.subtramites_configuration?.default_enabled || [];
  };

  // Función helper para verificar si hay subtramites por defecto configurados
  const hasDefaultSubtramites = () => {
    const defaultSubtramites = getDefaultSubtramites();
    return defaultSubtramites.length > 0;
  };

  // Función para limpiar el resultado de la última creación
  const clearLastResult = () => {
    setLastCreationResult(null);
  };

  return {
    // Estado
    isLoadingConfig,
    configError,
    hasConfigurationError: configError,
    lastCreationResult,

    // Acciones
    createTramiteWithDefaultSubtramites: createTramiteWithSubtramitesMutation.mutate,
    clearLastResult,

    // Estado de la mutation
    isCreating: createTramiteWithSubtramitesMutation.isPending,
    createError: createTramiteWithSubtramitesMutation.error,

    // Helpers
    getDefaultSubtramites,
    hasDefaultSubtramites,

    // Información de configuración
    defaultSubtramitesCount: getDefaultSubtramites().length,
    defaultSubtramites: getDefaultSubtramites()
  };
};

export default useDefaultSubtramites;